import {
  Home, Truck, Sofa, Refrigerator, Flame, Sparkles, Building2, Bath,
  Wind, Trash2, Boxes, Droplets,
} from "lucide-react";

export const NAV_LINKS = [
  { label: "Home", href: "#home" },
  { label: "Services", href: "#services" },
  { label: "Prices", href: "#prices" },
  { label: "How It Works", href: "#how-it-works" },
  { label: "Service Areas", href: "#service-areas" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

export const SERVICES = [
  { icon: Home, title: "End of Tenancy Cleaning", text: "Complete cleaning of an empty rental property before handing back the keys.", span: true },
  { icon: Truck, title: "Move-Out Cleaning", text: "Deep cleaning for tenants moving from a house or apartment." },
  { icon: Building2, title: "Landlord Cleaning", text: "Cleaning between tenants to prepare the property for new occupants." },
  { icon: Building2, title: "Apartment Cleaning", text: "Professional cleaning for studios and apartments of all sizes." },
  { icon: Home, title: "House Cleaning", text: "Detailed move-out cleaning for houses with two or more bedrooms." },
  { icon: Sparkles, title: "Deep Cleaning", text: "Intensive cleaning for properties requiring extra attention.", span: true },
  { icon: Flame, title: "Oven Cleaning", text: "Removal of grease and burnt-on dirt from the oven, trays and racks." },
  { icon: Droplets, title: "Carpet Cleaning", text: "Optional carpet cleaning priced separately depending on the number and size of rooms." },
  { icon: Refrigerator, title: "Fridge Cleaning", text: "Cleaning inside an empty and defrosted fridge or freezer." },
  { icon: Trash2, title: "Rubbish & Furniture Removal", text: "Optional removal of unwanted furniture, bags, boxes and household rubbish. Quoted separately after receiving photos." },
];

export const PRICING = {
  apartments: [
    { label: "Studio apartment", price: "€140" },
    { label: "1-bedroom apartment", price: "€180" },
    { label: "2-bedroom apartment", price: "€250" },
    { label: "3-bedroom apartment", price: "€320" },
  ],
  houses: [
    { label: "2-bedroom house", price: "€250" },
    { label: "3-bedroom house", price: "€320" },
    { label: "4-bedroom house", price: "€400" },
    { label: "5-bedroom house", price: "€500" },
    { label: "Larger properties", price: "Quote after photos" },
  ],
  extras: [
    { label: "Oven cleaning", price: "€50" },
    { label: "Carpet cleaning", price: "€35 / room" },
    { label: "Fridge cleaning", price: "€25" },
    { label: "Exterior window cleaning", price: "€60" },
    { label: "Same-day / urgent booking", price: "€60 extra" },
    { label: "Heavy cleaning surcharge", price: "After photos" },
    { label: "Rubbish removal", price: "€130" },
    { label: "Furniture removal", price: "Quote after photos" },
  ],
};

export const INCLUDED = [
  {
    icon: Flame, title: "Kitchen",
    items: ["Worktops and surfaces", "Sink and taps", "Cupboard doors", "Inside empty cupboards", "Hob", "Extractor exterior", "Splashback", "Appliance exteriors", "Floor cleaning", "Bins emptied if requested"],
  },
  {
    icon: Bath, title: "Bathrooms",
    items: ["Toilet", "Shower", "Bathtub", "Sink", "Taps", "Mirrors", "Tiles", "Removal of light limescale", "Floors", "Accessible surfaces"],
  },
  {
    icon: Sofa, title: "Bedrooms & Living Areas",
    items: ["Dusting", "Skirting boards", "Doors and door frames", "Sockets and switches", "Accessible radiators", "Wardrobes and storage units if empty", "Vacuuming", "Mopping", "Interior window glass where accessible"],
  },
  {
    icon: Wind, title: "General Areas",
    items: ["Entrance area", "Stairs", "Hallways", "Cobweb removal", "Reachable surfaces", "Floors", "Internal doors"],
  },
];

export const HOW_IT_WORKS = [
  { n: "01", title: "Send Property Details", text: "Tell us the address area, number of bedrooms and number of bathrooms." },
  { n: "02", title: "Send Photos or Video", text: "Send clear photos or a short walkthrough video through WhatsApp." },
  { n: "03", title: "Receive Your Quote", text: "We confirm the price, availability and estimated cleaning time." },
  { n: "04", title: "Book Your Cleaning", text: "Choose a suitable date and receive booking confirmation." },
];

export const WHY_CHOOSE = [
  "Clear quotes before booking",
  "Flexible appointments",
  "Houses and apartments covered",
  "Optional oven and carpet cleaning",
  "Optional rubbish and furniture removal",
  "Fast communication through WhatsApp",
  "Local service across Dublin and Kildare",
  "No hidden services added without approval",
];

export const SERVICE_AREAS = {
  Dublin: ["Dublin City", "West Dublin", "North Dublin", "South Dublin", "Blanchardstown", "Clondalkin", "Tallaght", "Swords", "Lucan"],
  Kildare: ["County Kildare", "Leixlip", "Maynooth", "Celbridge"],
};

export const FAQS = [
  { q: "How much does end-of-tenancy cleaning cost?", a: "Prices start from €140 for a studio, €180 for a one-bedroom apartment and €320 for a three-bedroom house. The final price depends on the size and condition of the property." },
  { q: "Is the price fixed?", a: "The price is confirmed after receiving accurate property information, photos or a walkthrough video. Extra work is not added without the customer's agreement." },
  { q: "Do I need to provide cleaning products?", a: "No. The cleaning team brings normal cleaning products and equipment unless something specialist is required." },
  { q: "Does the property need to be empty?", a: "An empty property is strongly preferred. Personal belongings and unnecessary items can slow down the cleaning and may affect the price." },
  { q: "Is oven cleaning included?", a: "Standard exterior cleaning is included, but full internal oven cleaning is an optional extra unless included in the confirmed quote." },
  { q: "Do you clean carpets?", a: "Yes. Carpet cleaning can be added and is priced depending on the number and size of rooms." },
  { q: "Do you clean windows?", a: "Interior window glass can be included where safely accessible. Exterior windows are quoted separately." },
  { q: "Can you remove rubbish or furniture?", a: "Yes. Customers should send photos of everything that needs to be removed. Waste and furniture removal are priced separately." },
  { q: "Do you offer same-day cleaning?", a: "Same-day appointments may be available depending on the schedule and property size. An urgent-booking fee may apply." },
  { q: "How long does the cleaning take?", a: "It depends on the property size and condition. A small apartment may take several hours, while a large or heavily soiled house may require most of the day." },
  { q: "Do you guarantee the return of my deposit?", a: "No cleaning company can control a landlord's final decision. The service is designed to prepare the property to a professional standard for inspection." },
  { q: "Which areas do you cover?", a: "Dublin, County Kildare and surrounding areas, subject to availability." },
];

export const IMAGES = {
  hero: "https://images.unsplash.com/photo-1615800002234-05c4d488696c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwzfHxjbGVhbiUyMGhvbWUlMjBsaXZpbmclMjByb29tJTIwYnJpZ2h0fGVufDB8fHx8MTc4NTA3NzQwM3ww&ixlib=rb-4.1.0&q=85",
  emptyRoom: "https://images.unsplash.com/photo-1630699144919-681cf308ae82?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxicmlnaHQlMjBlbXB0eSUyMG1vZGVybiUyMGxpdmluZyUyMHJvb20lMjBjbGVhbnxlbnwwfHx8fDE3ODUyNjE4Nzd8MA&ixlib=rb-4.1.0&q=85",
  kitchen: "https://images.unsplash.com/photo-1649083048198-f57bd8fb7a3b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxjbGVhbiUyMG1vZGVybiUyMGtpdGNoZW4lMjBpbnRlcmlvciUyMHN1bmxpZ2h0fGVufDB8fHx8MTc4NTI2MTg3N3ww&ixlib=rb-4.1.0&q=85",
  bathroom: "https://images.unsplash.com/photo-1704428381342-ea9df943619e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxzcGFya2xpbmclMjBjbGVhbiUyMGJhdGhyb29tJTIwbW9kZXJufGVufDB8fHx8MTc4MTk1OTMzMnww&ixlib=rb-4.1.0&q=85",
  dublin: "https://images.unsplash.com/photo-1759759262016-404623881338?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxEdWJsaW4lMjBJcmVsYW5kJTIwY2l0eSUyMHN0cmVldCUyMGhvdXNlc3xlbnwwfHx8fDE3ODUyNjE4Nzd8MA&ixlib=rb-4.1.0&q=85",
};

export const LOCATIONS = {
  "end-of-tenancy-cleaning-dublin": { title: "End of Tenancy Cleaning Dublin", area: "Dublin", service: "End of Tenancy Cleaning" },
  "move-out-cleaning-dublin": { title: "Move Out Cleaning Dublin", area: "Dublin", service: "Move-Out Cleaning" },
  "landlord-cleaning-dublin": { title: "Landlord Cleaning Dublin", area: "Dublin", service: "Landlord Cleaning" },
  "apartment-cleaning-dublin": { title: "Apartment Cleaning Dublin", area: "Dublin", service: "Apartment Cleaning" },
  "end-of-tenancy-cleaning-leixlip": { title: "End of Tenancy Cleaning Leixlip", area: "Leixlip", service: "End of Tenancy Cleaning" },
  "end-of-tenancy-cleaning-lucan": { title: "End of Tenancy Cleaning Lucan", area: "Lucan", service: "End of Tenancy Cleaning" },
  "end-of-tenancy-cleaning-maynooth": { title: "End of Tenancy Cleaning Maynooth", area: "Maynooth", service: "End of Tenancy Cleaning" },
  "end-of-tenancy-cleaning-celbridge": { title: "End of Tenancy Cleaning Celbridge", area: "Celbridge", service: "End of Tenancy Cleaning" },
  "end-of-tenancy-cleaning-blanchardstown": { title: "End of Tenancy Cleaning Blanchardstown", area: "Blanchardstown", service: "End of Tenancy Cleaning" },
  "end-of-tenancy-cleaning-tallaght": { title: "End of Tenancy Cleaning Tallaght", area: "Tallaght", service: "End of Tenancy Cleaning" },
};
